import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import CurrencySelector from "./components/CurrencySelector";
import API_BASE_URL from "./config";
import { formatDuration, formatStorageBytes } from "./formatDuration";
import { formatPrice, detectCurrency } from "./currencyUtils";
import "./Wallet.css";
import submitPaymentForm from "./desktop/submitPaymentForm";

function Subscribe() {
  const { planSlug } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [plan, setPlan] = useState(null);
  const [storageTiers, setStorageTiers] = useState([]);
  const [selectedTierSlug, setSelectedTierSlug] = useState("1tb");
  // A renewal-reminder email deep-links here with ?cycle=<monthly|yearly> so the
  // studio lands on the same cycle their expiring plan is on; fall back to the
  // per-plan default otherwise.
  const [billingCycle, setBillingCycle] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("cycle");
    if (q === "monthly" || q === "yearly") return q;
    return planSlug === "pro_max_ultra" ? "yearly" : "monthly";
  });
  const [walletBalance, setWalletBalance] = useState(0);
  const [currentStorage, setCurrentStorage] = useState(null); // { max_storage_bytes, extra_storage_bytes, storage_tier_slug }
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [walletPayLoading, setWalletPayLoading] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  const userId = localStorage.getItem("userId");
  const adminToken = localStorage.getItem("admin_token");
  const [adminActivateLoading, setAdminActivateLoading] = useState(false);
  const [adminConfirm, setAdminConfirm] = useState(false);
  const [adminSuccess, setAdminSuccess] = useState(false);
  const [currency, setCurrency] = useState(() => detectCurrency());
  const [exchangeRates, setExchangeRates] = useState([]);

  const isUltra = planSlug === "pro_max_ultra";

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/exchange-rates`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setExchangeRates(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleCurrencyChange = (c) => {
    setCurrency(c);
    localStorage.setItem("photentra_currency", c);
  };

  useEffect(() => {
    const fetches = [
      fetch(`${API_BASE_URL}/api/subscription-plans`).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/wallet/${userId}`).then((r) => r.json()).catch(() => ({})),
    ];
    if (isUltra) {
      fetches.push(fetch(`${API_BASE_URL}/api/storage-tiers`).then((r) => r.json()).catch(() => []));
      fetches.push(fetch(`${API_BASE_URL}/api/users/${userId}/storage-usage`).then((r) => r.json()).catch(() => null));
    }

    Promise.all(fetches)
      .then(([plansData, walletData, tiersData, storageData]) => {
        const found = (Array.isArray(plansData) ? plansData : []).find((p) => p.slug === planSlug);
        if (!found) { setError("Plan not found"); }
        else { setPlan(found); }
        setWalletBalance(walletData.balance || 0);
        if (tiersData && Array.isArray(tiersData)) {
          setStorageTiers(tiersData);
          const currentSlug = storageData?.has_storage_limit ? storageData.storage_tier_slug : null;
          // An expired/in-grace Ultra subscriber is renewing, not topping up: default to the
          // tier they already have rather than upselling them to the next one up.
          const planExpired = !!(storageData && (storageData.in_grace_period || storageData.is_locked));
          if (tiersData.length > 0) {
            if (currentSlug && planExpired) {
              setSelectedTierSlug(currentSlug);
            } else if (currentSlug) {
              const currentIdx = tiersData.findIndex(t => t.slug === currentSlug);
              const nextTier = tiersData[currentIdx + 1] || tiersData[tiersData.length - 1];
              setSelectedTierSlug(nextTier.slug);
            } else {
              setSelectedTierSlug(tiersData[0].slug);
            }
          }
        }
        if (storageData && storageData.has_storage_limit) setCurrentStorage(storageData);
      })
      .catch(() => setError("Failed to load plan"))
      .finally(() => setLoading(false));
  }, [planSlug, userId, isUltra]);

  const selectedTier = storageTiers.find((t) => t.slug === selectedTierSlug) || null;

  // True when the user already has Pro Max Ultra AND their plan is still active — this is an
  // additive storage top-up, not a purchase. Determined by whether storage-usage returned a
  // live cap. A lapsed subscriber (grace/locked) is instead renewing: they must go through the
  // full subscribe flow (pay-from-wallet / initiate-payment), which resets
  // subscription_purchased_at. Treating a renewal as a storage upgrade only moved the tier and
  // left the plan expired — that was the "charged but subscription not activated" bug.
  const isPlanExpired = !!(currentStorage && (currentStorage.in_grace_period || currentStorage.is_locked));
  const isStorageUpgrade = isUltra && currentStorage !== null && !isPlanExpired;

  // In Google-style model: upgrading replaces the tier. New cap = selected tier's bytes.
  const totalAfterUpgrade = isStorageUpgrade && selectedTier ? parseInt(selectedTier.bytes, 10) : null;

  // For Pro Max Ultra: price comes from the selected storage tier.
  // For other plans: price comes from the plan row.
  const getActivePrice = () => {
    if (isUltra && selectedTier) {
      return parseFloat(billingCycle === "yearly" ? selectedTier.price_yearly : selectedTier.price_monthly);
    }
    if (!plan) return 0;
    return parseFloat(billingCycle === "yearly" && plan.price_yearly != null ? plan.price_yearly : plan.price);
  };

  const activePrice = getActivePrice();
  const monthlyEquivalent = isUltra && selectedTier && selectedTier.price_yearly > 0
    ? parseFloat(selectedTier.price_yearly) / 12
    : (plan?.price_yearly != null ? parseFloat(plan.price_yearly) / 12 : null);
  const yearlySavings = isUltra && selectedTier
    ? (parseFloat(selectedTier.price_monthly) * 12 - parseFloat(selectedTier.price_yearly))
    : (plan?.price_yearly != null ? parseFloat(plan.price) * 12 - parseFloat(plan.price_yearly) : null);

  const handlePay = async () => {
    setPaymentLoading(true);
    setError(null);
    try {
      const endpoint = isStorageUpgrade
        ? `${API_BASE_URL}/api/subscription/upgrade-storage`
        : `${API_BASE_URL}/api/subscription/initiate-payment`;
      const body = isStorageUpgrade
        ? { user_id: parseInt(userId), storage_tier_slug: selectedTierSlug, billing_cycle: billingCycle, currency }
        : { user_id: parseInt(userId), plan_slug: planSlug, billing_cycle: billingCycle, storage_tier_slug: isUltra ? selectedTierSlug : undefined, currency };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Failed to initiate payment"); setPaymentLoading(false); return; }

      submitPaymentForm(formRef.current, data.payuUrl, data.params);
    } catch (err) {
      setError("Network error. Please try again.");
      setPaymentLoading(false);
    }
  };

  const doAdminActivate = async () => {
    setAdminActivateLoading(true);
    setError(null);
    try {
      if (isStorageUpgrade) {
        // Admin adding storage to existing ultra subscriber — additive, not replacement
        const res = await fetch(`${API_BASE_URL}/api/subscription/upgrade-storage`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ user_id: parseInt(userId), storage_tier_slug: selectedTierSlug, billing_cycle: billingCycle, pay_via: "admin" }),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.message || "Failed to upgrade storage"); return; }
        setAdminSuccess(true);
        setTimeout(() => navigate(`/wallet?storage_upgraded=1`), 1800);
      } else {
        const body = { plan_slug: planSlug, billing_cycle: billingCycle, note: "Activated by admin (no payment)" };
        if (isUltra && selectedTierSlug) body.storage_tier_slug = selectedTierSlug;
        const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/subscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.message || "Failed to activate plan"); return; }
        setAdminSuccess(true);
        setTimeout(() => navigate(`/dashboard?subscription=success&plan=${planSlug}`), 1800);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setAdminActivateLoading(false);
    }
  };

  const handleAdminActivate = () => setAdminConfirm(true);

  const handlePayFromWallet = async () => {
    setWalletPayLoading(true);
    setError(null);
    try {
      const endpoint = isStorageUpgrade
        ? `${API_BASE_URL}/api/subscription/upgrade-storage`
        : `${API_BASE_URL}/api/subscription/pay-from-wallet`;
      const body = isStorageUpgrade
        ? { user_id: parseInt(userId), storage_tier_slug: selectedTierSlug, billing_cycle: billingCycle, pay_via: "wallet" }
        : { user_id: parseInt(userId), plan_slug: planSlug, billing_cycle: billingCycle, storage_tier_slug: isUltra ? selectedTierSlug : undefined };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Failed to pay from wallet"); setWalletPayLoading(false); return; }
      navigate(isStorageUpgrade ? `/wallet?storage_upgraded=1` : `/dashboard?subscription=success&plan=${planSlug}`);
    } catch (err) {
      setError("Network error. Please try again.");
      setWalletPayLoading(false);
    }
  };

  return (
    <div className="wlt-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="wlt-main-content">
        <header className="wlt-header">
          <div className="wlt-header-left">
            <button className="wlt-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="wlt-page-title">Subscribe</h1>
          </div>
        </header>

        <main className="wlt-main">
          {adminSuccess && (
            <div className="wlt-payment-banner wlt-payment-banner--success">
              <span className="wlt-payment-banner__icon">✅</span>
              <span>Plan activated successfully! Redirecting to dashboard...</span>
            </div>
          )}

          {loading ? (
            <CameraLoader text="Loading plan..." />
          ) : !plan ? (
            <div className="wlt-payment-banner wlt-payment-banner--error">
              <span>Plan not found.</span>
              <button className="wlt-payment-banner__close" onClick={() => navigate("/dashboard")}>✕</button>
            </div>
          ) : (
            <div className="wlt-add-money-section">
              <div className="wlt-add-money-card" style={isUltra ? { borderColor: "#f59e0b", borderWidth: 2 } : undefined}>
                <div className="wlt-add-money-card__header">
                  <h3>{plan.name} Plan {isUltra && <span style={{ fontSize: 12, background: "linear-gradient(90deg,#f59e0b,#d97706)", color: "#fff", padding: "2px 8px", borderRadius: 999, marginLeft: 6 }}>Ultimate</span>}</h3>
                  <button className="wlt-add-money-card__close" onClick={() => navigate("/dashboard")}>✕</button>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <CurrencySelector value={currency} onChange={handleCurrencyChange} rates={exchangeRates} />
                </div>

                {/* Storage tier selector — Pro Max Ultra */}
                {isUltra && storageTiers.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {isStorageUpgrade ? "Upgrade Storage" : "Storage Capacity"}
                    </label>
                    {isStorageUpgrade && (
                      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                        Current plan: <strong>{currentStorage.storage_tier_name || currentStorage.storage_tier_slug}</strong> · Select a larger tier to upgrade:
                      </p>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {storageTiers.map((tier) => {
                        const isCurrentTier = isStorageUpgrade && tier.slug === currentStorage?.storage_tier_slug;
                        const currentIdx = isStorageUpgrade ? storageTiers.findIndex(t => t.slug === currentStorage?.storage_tier_slug) : -1;
                        const tierIdx = storageTiers.findIndex(t => t.slug === tier.slug);
                        const isDisabled = isStorageUpgrade && tierIdx <= currentIdx;
                        return (
                          <button
                            key={tier.slug}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => !isDisabled && setSelectedTierSlug(tier.slug)}
                            style={{
                              padding: "10px 18px", borderRadius: 10, fontSize: 14, fontWeight: 700,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.4 : 1,
                              border: selectedTierSlug === tier.slug ? "2px solid #f59e0b" : "1.5px solid #e5e7eb",
                              background: isCurrentTier ? "#f3f4f6" : selectedTierSlug === tier.slug ? "#fffbeb" : "#fff",
                              color: isCurrentTier ? "#9ca3af" : selectedTierSlug === tier.slug ? "#b45309" : "#374151",
                              transition: "all 0.15s",
                            }}
                          >
                            {tier.name}
                            {isCurrentTier && (
                              <span style={{ display: "block", fontSize: 10, fontWeight: 500, color: "#9ca3af", marginTop: 2 }}>Current</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {isStorageUpgrade && selectedTier && !storageTiers.find(t => t.slug === selectedTierSlug && t.slug === currentStorage?.storage_tier_slug) && (
                      <p style={{ fontSize: 13, color: "#d97706", margin: "8px 0 0", fontWeight: 600 }}>
                        Upgrading: {currentStorage.storage_tier_name || currentStorage.storage_tier_slug} → {selectedTier.name}
                      </p>
                    )}
                    {selectedTier && !isStorageUpgrade && (
                      <p style={{ fontSize: 12, color: "#9ca3af", margin: "6px 0 0" }}>
                        {formatStorageBytes(selectedTier.bytes)} storage included
                      </p>
                    )}
                  </div>
                )}

                {/* Billing cycle toggle */}
                {(isUltra || plan.price_yearly != null) && (
                  <div style={{ display: "flex", gap: 8, margin: "4px 0 16px" }}>
                    {!isUltra && (
                      <button
                        type="button"
                        onClick={() => setBillingCycle("monthly")}
                        style={{
                          flex: 1, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
                          border: billingCycle === "monthly" ? "2px solid #7c3aed" : "1px solid #d1d5db",
                          background: billingCycle === "monthly" ? "#f5f3ff" : "#fff",
                          color: billingCycle === "monthly" ? "#7c3aed" : "#374151",
                        }}
                      >
                        Monthly
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setBillingCycle("yearly")}
                      style={{
                        flex: 1, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
                        border: billingCycle === "yearly" ? (isUltra ? "2px solid #f59e0b" : "2px solid #7c3aed") : "1px solid #d1d5db",
                        background: billingCycle === "yearly" ? (isUltra ? "#fffbeb" : "#f5f3ff") : "#fff",
                        color: billingCycle === "yearly" ? (isUltra ? "#b45309" : "#7c3aed") : "#374151",
                      }}
                    >
                      Yearly {yearlySavings > 0 && <span style={{ color: "#16a34a" }}>· save {formatPrice(yearlySavings, currency, exchangeRates)}</span>}
                    </button>
                    {isUltra && (
                      <button
                        type="button"
                        onClick={() => setBillingCycle("monthly")}
                        style={{
                          flex: 1, padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
                          border: billingCycle === "monthly" ? "2px solid #f59e0b" : "1px solid #d1d5db",
                          background: billingCycle === "monthly" ? "#fffbeb" : "#fff",
                          color: billingCycle === "monthly" ? "#b45309" : "#374151",
                        }}
                      >
                        Monthly
                      </button>
                    )}
                  </div>
                )}

                {billingCycle === "yearly" && monthlyEquivalent != null && monthlyEquivalent > 0 && (
                  <p className="wlt-pay-note" style={{ marginBottom: 8 }}>
                    {formatPrice(monthlyEquivalent, currency, exchangeRates)}/mo billed as one yearly payment — 2 months free vs. paying monthly.
                  </p>
                )}

                {/* Feature list */}
                <ul style={{ margin: "8px 0 16px", paddingLeft: 20, color: "inherit" }}>
                  <li>Valid for {billingCycle === "yearly" ? "12 months" : (formatDuration(plan.duration_days) || "30 days")}</li>
                  <li>{plan.max_events === null ? "Unlimited events" : `${plan.max_events} events/month`}</li>
                  <li>{plan.max_photos_per_event === null ? "Unlimited photo uploads" : `${plan.max_photos_per_event} photos per event`}</li>
                  {isUltra && selectedTier && <li>{formatStorageBytes(selectedTier.bytes)} storage</li>}
                  {!isUltra && plan.max_storage_bytes && <li>{formatStorageBytes(plan.max_storage_bytes)} storage</li>}
                  <li>{isUltra ? "Original quality — zero compression" : (plan.max_uncompressed_events ? `${plan.max_uncompressed_events} original-quality events/month` : "Smart compression to save storage")}</li>
                  {isUltra && <li>Unlimited team members</li>}
                  {isUltra && <li>Priority support</li>}
                </ul>

                {/* Price display */}
                <div style={{ marginBottom: 16, padding: "14px 16px", background: isUltra ? "#fffbeb" : "#f5f3ff", borderRadius: 10 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: isUltra ? "#b45309" : "#7c3aed" }}>
                    {activePrice > 0 ? formatPrice(activePrice, currency, exchangeRates) : "Pricing on request"}
                    {activePrice > 0 && <span style={{ fontSize: 14, fontWeight: 500, color: "#6b7280", marginLeft: 6 }}>/{billingCycle === "yearly" ? "year" : "month"}</span>}
                  </div>
                  {isUltra && selectedTier && (
                    <p style={{ fontSize: 12, color: "#92400e", margin: "4px 0 0" }}>
                      {formatStorageBytes(selectedTier.bytes)} · {billingCycle === "yearly" ? "Yearly billing" : "Monthly billing"}
                    </p>
                  )}
                </div>

                {error && <p className="wlt-pay-note" style={{ color: "#e53e3e" }}>{error}</p>}

                {adminToken && (
                  <>
                    <button className="wlt-pay-btn" disabled={adminActivateLoading} onClick={handleAdminActivate} style={{ background: "#7c3aed", marginBottom: 8 }}>
                      {adminActivateLoading ? "Activating..." : `Admin: Activate ${plan.name} (No Payment)`}
                    </button>
                    <p className="wlt-pay-note" style={{ color: "#7c3aed", marginBottom: 16 }}>Admin mode — activates instantly, no payment deducted</p>
                  </>
                )}

                {walletBalance >= activePrice && activePrice > 0 && (
                  <>
                    <button className="wlt-pay-btn" disabled={walletPayLoading || paymentLoading} onClick={handlePayFromWallet}>
                      {walletPayLoading ? "Activating..." : `Pay ${formatPrice(activePrice, "INR", exchangeRates)} from Wallet (₹${walletBalance.toFixed(2)} available)`}
                    </button>
                    <p className="wlt-pay-note" style={{ marginBottom: 14 }}>Deducted instantly from your wallet balance, no redirect.</p>
                  </>
                )}

                <button
                  className={walletBalance >= activePrice && activePrice > 0 ? "wlt-add-money-btn" : "wlt-pay-btn"}
                  disabled={paymentLoading || walletPayLoading}
                  onClick={handlePay}
                  style={walletBalance >= activePrice && activePrice > 0 ? { width: "100%", background: isUltra ? "linear-gradient(135deg,#f59e0b,#d97706)" : undefined } : (isUltra ? { background: "linear-gradient(135deg,#f59e0b,#d97706)" } : undefined)}
                >
                  {paymentLoading
                    ? "Redirecting to PayU..."
                    : (activePrice > 0 ? `Pay ${formatPrice(activePrice, currency, exchangeRates)} via PayU` : "Contact us to subscribe")}
                </button>
                <p className="wlt-pay-note">{activePrice > 0 ? "You will be redirected to PayU secure payment page" : "Pricing on request — contact photentra@gmail.com"}</p>
              </div>
            </div>
          )}
        </main>
      </div>
      <form ref={formRef} style={{ display: "none" }} />

      {adminConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setAdminConfirm(false)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 380, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 22 }}>🔐</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#7c3aed" }}>Admin Action</h3>
            </div>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
              Activate the <strong>{plan?.name}</strong> plan{isUltra && selectedTier ? ` (${selectedTier.name} storage)` : ""} for this studio?<br /><br />
              This bypasses payment — the plan will start immediately at no charge.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setAdminConfirm(false)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 14 }}>Cancel</button>
              <button onClick={() => { setAdminConfirm(false); doAdminActivate(); }} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscribe;
